import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

import NewReleaseModal from '../../redux/containers/newsletter/NewReleaseModal';
import ReleaseCard from '../../redux/containers/newsletter/ReleaseCard';

const labels = ['Fix', 'Hotfix', 'Feature', 'Update', 'Config', 'Rollout', 'Refactor'].sort();


function NewsletterView(props) {
  const {
    getReleases,
    getTeamList,
    getSoftwareList,
    getSoftwares,
    isPostingRelease,
    getPostedRelease,
    getUpdatedRelease,
    isUpdatingRelease,
    releaseDatesList,
    requestReleasesDatesList,
    labelList,
    requestLabelList
  } = props;

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showingReleases, setShowingReleases] = useState([]);
  const [loadingNode, setLoadingNode] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(3);

  let releasesRef = useRef({});
  let pageRef = useRef(1);
  let hasNextPageRef = useRef(true);
  let isFetchingRef = useRef(false);
  let requestIdRef = useRef(0);
  let textRef = useRef('');
  let selectedLabelsRef = useRef([]);
  let selectedSoftwaresRef = useRef([]);
  let selectedDateRef = useRef("");

  releasesRef.current = showingReleases;
  hasNextPageRef.current = hasNextPage;

  const fetchReleases = async (page, isFirst) => {
    if (!isFirst && isFetchingRef.current) {
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    isFetchingRef.current = true;

    try {
      const { data, has_next_page } = await getReleases(
        page,
        selectedLabelsRef.current,
        selectedSoftwaresRef.current,
        textRef.current,
        selectedDateRef.current
      );
      if (requestId !== requestIdRef.current) {
        return;
      }
      pageRef.current = page + 1;
      isFirst
        ? setShowingReleases([...data])
        : setShowingReleases([...releasesRef.current, ...data]);
      setHasNextPage(has_next_page);
    } catch (error) {
      console.log(error);
    } finally {
      if (requestId === requestIdRef.current) {
        isFetchingRef.current = false;
      }
    }
  };

  const firstFetch = async () => {
    setHasNextPage(true);
    await requestReleasesDatesList(selectedHistory);
    await fetchReleases(1, true);
  };

  const handleSelectDate = (selectedDate) => {
    selectedDateRef.current = selectedDate;
    setHasNextPage(true);
    fetchReleases(1, true);
  };
  const handleSelectHistory = (value) => {
    setSelectedHistory(value);
    requestReleasesDatesList(value);
  };

  useEffect(() => {
    selectedLabelsRef.current = [];
    selectedSoftwaresRef.current = [];
    textRef.current = '';
    requestLabelList()
    firstFetch();
  }, [getPostedRelease, getUpdatedRelease]);

  useEffect(() => {
    getSoftwares();
  }, [getSoftwares]);

  const handleObserver = entries => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPageRef.current && !isFetchingRef.current) {
      fetchReleases(pageRef.current, false);
    }
  };

  useEffect(() => {
    if (!loadingNode) {
      return undefined;
    }
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '200px',
      threshold: 0,
    });
    observer.observe(loadingNode);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingNode, showingReleases.length, hasNextPage]);

  const handleLabelClick = title => {
    if (selectedLabelsRef.current.includes(title)) {
      let filteredArray = selectedLabelsRef.current.filter(
        item => item !== title,
      );
      selectedLabelsRef.current = filteredArray;
    } else {
      selectedLabelsRef.current = [...selectedLabelsRef.current, title];
    }
    firstFetch();
  };

  const handleSoftwareClick = title => {

    if (title) {
      if (selectedSoftwaresRef.current.includes(title.name)) {
        let filteredArray = selectedSoftwaresRef.current.filter(
          item => item !== title.name,
        );
        selectedSoftwaresRef.current = filteredArray;
      } else {
        selectedSoftwaresRef.current = [title.name];
      }
    }
    else {
      selectedSoftwaresRef.current = []
    }
    firstFetch();
  };

  return (
    <>
      <NewReleaseModal
        key={new Date().getTime()}
        isFormOpen={isFormOpen}
        setIsFormOpen={setIsFormOpen}
        teams={getTeamList}
      />

      {isPostingRelease || isUpdatingRelease ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            height: '100vh',
          }}>
          <CircularProgress color="error" />{' '}
          {isPostingRelease ? 'Posting New Release...' : 'Updating Release...'}
        </Box>
      ) : (
        <div>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overFlow: 'auto',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 1,
                padding: 2,
                borderRadius: 3,
                position: 'sticky',
                backgroundColor: '#ffffff',
                zIndex: 100,
                width: '100%',
                alignSelf: 'flex-start',
                top: 70,
              }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Autocomplete
                    sx={{
                      width: 400,
                      marginX: 2,
                      marginBottom: 1,
                    }}
                    size='small'
                    options={getSoftwareList}
                    value={selectedSoftwaresRef.current.length === 0 ? null : selectedSoftwaresRef.current}
                    isOptionEqualToValue={(option, value) => value.name ? option.name.includes(value.name) : option.name.includes(value[0])}
                    getOptionLabel={(option) => option.name_with_namespace ? option.name_with_namespace : option[0]}
                    renderOption={(props, option) => (
                      <Box
                        sx={{
                          borderRadius: '8px',
                          margin: '5px',
                          flexDirection: 'column',

                        }}
                        component="li"
                        {...props}
                        key={option}
                      >
                        <Typography align='center'>
                          {option.name}
                        </Typography>

                        <Typography align='center' sx={{ fontSize: 15, opacity: 0.5 }}>
                          {option.name_with_namespace}
                        </Typography>
                      </Box>
                    )}
                    onChange={(e, value) => handleSoftwareClick(value)}
                    renderInput={params => <TextField {...params} label="Software" />}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "row", marginRight: 2 }}>
                  <TextField
                    label="Search..."
                    variant="outlined"
                    size="small"
                    onChange={e => (textRef.current = e.target.value)}
                    sx={{
                      marginX: 2,
                      marginBottom: 1,
                      width: 250,

                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    sx={{ marginBottom: 1 }}
                    onClick={firstFetch}>
                    Search
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  marginX: 2,
                  maxWidth: '100%',
                  overflowX: 'hidden',
                  flexWrap: 'wrap',
                  marginTop: 1,
                }}>
                {labelList.sort().map((label, idx) => {
                  return (
                    <Chip
                      onClick={e => handleLabelClick(e.target.innerText)}
                      clickable
                      key={idx}
                      label={label}
                      size="small"
                      color="info"
                      variant={
                        selectedLabelsRef.current.includes(label)
                          ? 'filled'
                          : 'outlined'
                      }
                      sx={{ fontSize: 15, margin: 0.2 }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                borderRadius: 1,
                gap: 2,

              }}>
              {showingReleases.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    width: '100%',
                  }}>
                  {showingReleases.map((release, i) => {
                    return (
                      <div
                        style={{ width: '100%' }}
                        key={i}
                        id={moment(release.created_at).format('Do MMMM YYYY')}>
                        <ReleaseCard
                          release={release}
                          handleLabelClick={handleLabelClick}
                          handleSoftwareClick={handleSoftwareClick}
                          selectedLabels={selectedLabelsRef.current}
                          selectedSoftwares={selectedSoftwaresRef.current}
                        />
                      </div>
                    );
                  })}
                  <Box
                    ref={setLoadingNode}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      marginY: 2,
                    }}>
                    {hasNextPage ? (
                      <>
                        <CircularProgress size={25} />
                        <span>Loading more releases...</span>
                      </>
                    ) : (
                      <>
                        <span>No more releases.</span>
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    paddingY: 2,
                    paddingX: 3,
                    marginTop: 2,
                    borderRadius: 3,
                    width: "80%"
                  }}>
                  {' '}
                  No matching releases{' '}
                </Box>
              )}
              {releaseDatesList.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: 5,
                    backgroundColor: '#ffffff',
                    borderRadius: 3,  
                    minWidth: 'fit-content',
                    minHeight: '65vh',
                    alignSelf: 'flex-start',
                    top: 219,
                    position: 'sticky',
                  }}>
                  <Button
                    onClick={() => setIsFormOpen(true)}
                    variant="contained"
                    startIcon={<EditIcon />}
                    sx={{
                      marginX: 2,
                      marginTop: 2,
                      marginBottom: 2,
                      borderRadius: 3,
                      align: "center"
                    }}>

                    Write new release...


                  </Button>

                  <Box
                    sx={{
                      marginX: 2,
                      marginTop: 2,
                    }}>
                    <FormControl sx={{ m: 1, minWidth: 170 }} size="small">
                      <InputLabel id="demo-select-small-label">History</InputLabel>
                      <Select
                        value={selectedHistory}
                        label="History"
                        onChange={(event) => handleSelectHistory(event.target.value)}
                      >
                        <MenuItem value={3}>Last 3 Months</MenuItem>
                        <MenuItem value={6}>Last 6 Months</MenuItem>
                        <MenuItem value={12}>Last Year</MenuItem>
                      </Select>
                    </FormControl>

                    <Box
                      sx={{
                        overflowY: 'scroll',
                        maxHeight: "50vh",
                      }}>
                      <List>
                        <ListItem disablePadding >
                          <ListItemButton
                            selected={selectedDateRef.current === ""}
                            onClick={() => handleSelectDate("")}>
                            <ListItemText >
                              <Box sx={{ color: "gray", fontSize: 5, display: "inline-flex" }}>
                                <KeyboardArrowRightIcon />
                                <Typography>All releases
                                </Typography>
                              </Box>

                            </ListItemText>
                          </ListItemButton>
                        </ListItem>

                        {releaseDatesList.map((date, i) => {
                          const formatedTime = moment(date).format('Do MMMM YYYY').toString()
                          return (
                            <ListItem disablePadding key={i}>
                              <ListItemButton
                                selected={selectedDateRef.current === date}
                                onClick={() => handleSelectDate(date)}>
                                <ListItemText >
                                  <Box sx={{ color: "gray", fontSize: 5, display: "inline-flex" }}>
                                    <KeyboardArrowRightIcon />
                                    <Typography  >{formatedTime}</Typography>
                                  </Box>

                                </ListItemText>
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  </Box>
                </Box>
              )}

            </Box>
          </Box>
        </div>
      )}
    </>
  );
}

export default NewsletterView;
